import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Grid, Paper, Typography, Stack, Button, LinearProgress, Chip, useTheme, useMediaQuery, Tooltip } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import TransactionFilters, { TransactionFiltersState } from '@/components/TransactionFilters'
import TransactionTable, { Column } from '@/components/TransactionTable'
import TransactionTablePagination from '@/components/TransactionTablePagination'
import TransactionDetailModal from '@/components/TransactionDetailModal'
import { WithdrawalTransaction, Withdrawal } from '@/types/transactions'
import { exportToCSV, exportToPDF } from '@/utils/export'
import { transactionService } from '@/services/transactionService'

const Withdrawals = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  
  const columns: Column<WithdrawalTransaction>[] = useMemo(() => [
    { header: 'MSISDN', field: 'msisdn' },
    { header: 'Amount', field: 'amount', render: (row) => `KES ${row.amount.toFixed(2)}` },
    ...(isTablet ? [] : [{ header: 'Net Amount', field: 'amountSent', render: (row: WithdrawalTransaction) => `KES ${(row.amountSent || 0).toFixed(2)}` }]),
    ...(isMobile ? [] : [{ header: 'Paybill', field: 'paybill' }]),
    { header: 'Channel', field: 'channel' },
    { header: 'Status', field: 'status' },
    { 
      header: 'BtoBet', 
      field: 'btobetResponse',
      render: (row) => {
        if (!row.btobetResponse) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }
        const chip = (
          <Chip 
            icon={<IntegrationInstructionsIcon />} 
            label={isMobile ? undefined : "Yes"}
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ 
              '& .MuiChip-icon': { 
                marginLeft: isMobile ? '4px' : '6px',
                marginRight: isMobile ? '4px' : '6px',
              },
              minWidth: isMobile ? '32px' : 'auto',
            }}
          />
        )
        return isMobile ? (
          <Tooltip title="BtoBet Response Available" arrow>
            {chip}
          </Tooltip>
        ) : chip
      }
    },
    { 
      header: 'M-Pesa', 
      field: 'mpesaResponse',
      render: (row) => {
        if (!row.mpesaResponse) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }
        const chip = (
          <Chip 
            icon={<IntegrationInstructionsIcon />} 
            label={isMobile ? undefined : "Yes"}
            size="small" 
            color="secondary" 
            variant="outlined"
            sx={{ 
              '& .MuiChip-icon': { 
                marginLeft: isMobile ? '4px' : '6px',
                marginRight: isMobile ? '4px' : '6px',
              },
              minWidth: isMobile ? '32px' : 'auto',
            }}
          />
        )
        return isMobile ? (
          <Tooltip title="M-Pesa Response Available" arrow>
            {chip}
          </Tooltip>
        ) : chip
      }
    },
    ...(isMobile ? [] : [{ header: 'Receipt', field: 'mpesaReceiptNumber' }]),
    ...(isTablet ? [] : [
      { header: 'Conversation', field: 'conversationId' },
      { header: 'Originator', field: 'originatorConversationId' },
    ]),
    { header: 'Created At', field: 'createdAt', render: (row) => isMobile ? new Date(row.createdAt).toLocaleDateString() : new Date(row.createdAt).toLocaleString() },
  ], [isMobile, isTablet])
  const [filters, setFilters] = useState<TransactionFiltersState>({
    msisdn: '',
    paybill: '',
    status: 'All',
    channel: 'All',
    externalId: '',
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<WithdrawalTransaction[]>([])
  const [total, setTotal] = useState(0)
  const [selectedTransaction, setSelectedTransaction] = useState<Withdrawal | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const buildQueryParams = useCallback(() => {
    const params: Record<string, string | number | undefined> = {
      page: page + 1,
      page_size: rowsPerPage,
    }
    if (filters.msisdn) params.msisdn = filters.msisdn
    if (filters.paybill) params.paybill = filters.paybill
    if (filters.status && filters.status !== 'All') params.status = filters.status
    if (filters.channel && filters.channel !== 'All') params.channel = filters.channel
    if (filters.externalId) params.external_transaction_id = filters.externalId
    return params
  }, [filters, page, rowsPerPage])

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true)
    try {
      const response = await transactionService.fetchWithdrawals(buildQueryParams())
      setData(response.items)
      setTotal(response.pagination.total)
    } catch (error) {
      console.error('Failed to load withdrawals', error)
    } finally {
      setLoading(false)
    }
  }, [buildQueryParams])

  useEffect(() => {
    fetchWithdrawals()
  }, [fetchWithdrawals])

  const totals = useMemo(() => {
    const totalAmount = data.reduce((acc, item) => acc + item.amount, 0)
    const completed = data.filter((item) => item.status === 'COMPLETED').length
    const failed = data.filter((item) => item.status === 'FAILED').length
    return { totalAmount, completed, failed }
  }, [data])

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRowClick = (row: WithdrawalTransaction) => {
    const withdrawal: Withdrawal = {
      id: row.id,
      msisdn: row.msisdn,
      amount: row.amount,
      amount_sent: row.amountSent || row.amount,
      applied_tax: row.appliedTax || 0,
      paybill: row.paybill || '',
      mpesa_receipt_number: row.mpesaReceiptNumber || null,
      conversation_id: row.conversationId || null,
      originator_conversation_id: row.originatorConversationId || null,
      channel_id: 0,
      status_id: 0,
      external_transaction_id: row.externalTransactionId,
      external_user_id: row.externalUserId || null,
      sms_withdrawal_request_id: row.smsWithdrawalRequestId || null,
      comment: row.comment || null,
      btobet_response: row.btobetResponse || null,
      mpesa_response: row.mpesaResponse || null,
      channel: { id: 0, name: row.channel || 'N/A', description: null },
      status: { id: 0, name: row.status || 'PENDING', description: null },
      created_at: row.createdAt,
      updated_at: row.createdAt, // Use createdAt if updatedAt not available
    }
    setSelectedTransaction(withdrawal)
    setModalOpen(true)
  }

  const handleExportCSV = () => {
    exportToCSV(data, `withdrawals_${new Date().toISOString().split('T')[0]}`, columns)
  }

  const handleExportPDF = () => {
    exportToPDF(
      data,
      `withdrawals_${new Date().toISOString().split('T')[0]}`,
      'Withdrawal Transactions',
      columns
    )
  }

  const handleFilterChange = (updated: TransactionFiltersState) => {
    setFilters(updated)
    setPage(0)
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Withdrawals
      </Typography>

      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Amount
            </Typography>
            <Typography variant="h5">KES {totals.totalAmount.toFixed(2)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Completed
            </Typography>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="h5">{totals.completed}</Typography>
              <Typography variant="body2" color="text.secondary">
                / {data.length}
              </Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Failed
            </Typography>
            <Typography variant="h5">{totals.failed}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <TransactionFilters onFilterChange={handleFilterChange} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCSV}
          size="small"
        >
          Export CSV
        </Button>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleExportPDF}
          size="small"
        >
          Export PDF
        </Button>
      </Box>

      <TransactionTable
        title="Withdrawal Transactions"
        columns={columns}
        data={data}
        onRowClick={handleRowClick}
      />

      <TransactionTablePagination
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <TransactionDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        transaction={selectedTransaction}
        type="withdrawal"
      />
    </Box>
  )
}

export default Withdrawals
